import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {ComponentType} from '@angular/cdk/portal';

import {
    FundMandatoryProductPriceTypesInterface,
    FundProductInterface, FundProductPriceModel,
    FundProductPricesService,
    FundProductTypesEnum,
    FundProductWithPricesModel,
    HttpStatusCodesEnum, ProductCategory,
    ProductFieldNames
} from '@spa/api';

import {PanelPaths} from 'appShared/paths';

import {EventsService} from 'appEntries/panel/services';

import {
    AddEditProductPriceDialogConfig,
    AddEditProductPriceDialogWidth,
    PanelSettingsProductsDataTableItems
} from '../../../../../assets/descriptors';
import {ReportTableDataRow} from 'appShared/components';
import {Helper} from 'appShared/classes';
import {Observable, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {AlertsService} from 'appShared/services';
import {AlertTypesEnum} from 'appShared/enums';

export class PanelSettingsProductsTrait {
    private constructor() {
    }

    static isProductCertificate(product: FundProductInterface): boolean {
        return FundProductTypesEnum.InvestmentCertificate === product.type
    }

    static isProductBonds(product: FundProductInterface): boolean {
        return FundProductTypesEnum.Bonds === product.type
    }

    static isProductCertificateOrBond(product: FundProductInterface): Boolean {
        return [FundProductTypesEnum.InvestmentCertificate, FundProductTypesEnum.Bonds]
            .indexOf(product.type) >= 0;
    }

    static isBonds(productType: FundProductTypesEnum): boolean {
        return FundProductTypesEnum.Bonds === productType
    }

    static isCertificate(productType: FundProductTypesEnum): boolean {
        return FundProductTypesEnum.InvestmentCertificate === productType
    }

    static isCertificateOrBond(productType: FundProductTypesEnum): boolean {
        return [FundProductTypesEnum.InvestmentCertificate, FundProductTypesEnum.Bonds]
            .indexOf(productType) >= 0;
    }

    static mapProductsTableData(models: FundProductInterface[]): ReportTableDataRow[] {
        return models.map(product => {
            const row = new ReportTableDataRow({rowId: product.code, dataItems: []});

            row.dataItems = PanelSettingsProductsDataTableItems().map(item => {
                switch (item.colId) {
                    case ProductFieldNames.ProductId:
                        item.value = product.productId;
                        break;
                    case ProductFieldNames.Name:
                        item.value = product.name;
                        break;
                    case ProductFieldNames.Code:
                        item.value = product.code;
                        break;
                    case ProductFieldNames.Type:
                        item.value = Helper.textsHelper().getProductTypeName(product.type);
                        break;
                    case ProductFieldNames.IssuedOn:
                        item.value = Helper.formatDate(product.issuedOn);
                        break;
                    case ProductFieldNames.ExpiresOn:
                        item.value = Helper.formatDate(product.expiresOn);
                        break;
                    case ProductFieldNames.Status:
                        item.value = Helper.textsHelper().getProductStatusName(product.status);
                        break;
                    case ProductFieldNames.CreatedBy:
                        item.value = product.createdBy;
                        break;
                    default:
                        item.value = '';
                        break;
                }

                return item;
            });

            return row;
        });
    }

    static openAddEditPriceDialog(
        alertsService: AlertsService,
        fundOrderPricesService: FundProductPricesService,
        dialog: MatDialog,
        component: ComponentType<any>,
        product: FundProductInterface,
        prices: FundProductPriceModel[],
        isAddNew = false,
        updatingDate: string | null = null,
    ): Observable<any> {
        return fundOrderPricesService.getMandatoryPriceTypes(product.id)
            .pipe(switchMap(response => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    alertsService.addAlert('Cannot fetch mandatory price types for this product.', AlertTypesEnum.Error);

                    return of(null);
                }

                const mandatoryTypes = response.data as FundMandatoryProductPriceTypesInterface;

                if (!mandatoryTypes || !mandatoryTypes.priceTypes || mandatoryTypes.priceTypes.length === 0) {
                    const label = isAddNew ? 'creating' : 'editing';
                    alertsService.addAlert(`Price ${label} is not applicable for this product.`, AlertTypesEnum.Warning);

                    return of(null);
                }

                return dialog.open(component, {
                    width: AddEditProductPriceDialogWidth,
                    data: {
                        product,
                        prices,
                        isAddNew,
                        updatingDate,
                        mandatoryTypes,
                        config: AddEditProductPriceDialogConfig(isAddNew),
                    },
                    disableClose: true
                }).afterClosed();
            }));
    }

    static quickActionsSuccessAfterClosedHandler(
        product: FundProductInterface,
        desiredPath: string,
        data: FundProductPriceModel[],
        eventsService: EventsService,
        router: Router
    ): void {
        const currentUrl = router.url;
        const regex = new RegExp(`^.*${desiredPath}$`);

        if (!regex.test(currentUrl)) {
            const path = [
                PanelPaths.root,
                PanelPaths.children.settings.path,
                PanelPaths.children.settings.children.products.path,
                PanelPaths.children.settings.children.products.children.edit.path,
                product.id,
                product.code,
                PanelPaths.children.settings.children.products.children.edit.children.prices.path,
            ].join('/');

            router.navigate([path])
                .finally();
        } else {
            eventsService.forceProductWithPricesGet.next(data);
        }
    }
}
